import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { stockApi } from '../api/stock-api';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect } from '../components/SearchableSelect';
import { stockUnitLabels, stockUsageLabels, stockUsages } from '../domain/fulfillment';
import {
  isBelowMinimum,
  locationTypeLabels,
  stockTotals,
  type LocationType,
  type WarehouseLocation,
} from '../domain/stock';
import { LocationDrawer } from '../features/stock/LocationDrawer';
import { MovementList } from '../features/stock/MovementList';
import { StockItemDrawer } from '../features/stock/StockItemDrawer';
import { useStockReferences } from '../features/stock/use-stock-references';

const tabs = ['items', 'locations', 'movements'] as const;
// 'unset' finds items nobody has classified yet.
const usageFilters = [...stockUsages, 'unset'] as const;
type UsageFilter = (typeof usageFilters)[number];

function isUsageFilter(value: string | null): value is UsageFilter {
  return usageFilters.some((filter) => filter === value);
}
type Tab = (typeof tabs)[number];
const tabLabels: Record<Tab, string> = {
  items: 'Stock items',
  locations: 'Locations',
  movements: 'Movements',
};

function isTab(value: string | null): value is Tab {
  return tabs.some((tab) => tab === value);
}

/** Quantity-controlled stock: catalog, locations and ledger (tidaltech/koi#8096). */
export function StockPage() {
  const references = useStockReferences();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: Tab = isTab(tabParam) ? tabParam : 'items';
  const query = params.get('q') ?? '';
  const showInactive = params.get('inactive') === 'true';
  const usageParam = params.get('usage');
  const usageFilter: UsageFilter | null = isUsageFilter(usageParam) ? usageParam : null;
  const belowMinimumOnly = params.get('belowMinimum') === 'true';
  const [editing, setEditing] = useState<'new-item' | 'new-location' | WarehouseLocation | null>(
    null,
  );

  function setParam(name: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    setParams(next);
  }

  const items = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return references.items
      .filter((item) => showInactive || item.active)
      .filter(
        (item) =>
          !usageFilter || (usageFilter === 'unset' ? !item.usage : item.usage === usageFilter),
      )
      .filter(
        (item) =>
          !belowMinimumOnly ||
          isBelowMinimum(item, stockTotals(references.balances, item.id).onHand),
      )
      .filter(
        (item) =>
          !needle ||
          `${item.sku} ${item.displayName} ${item.barcodes.join(' ')}`
            .toLowerCase()
            .includes(needle),
      )
      .sort((a, b) => a.sku.localeCompare(b.sku));
  }, [references.items, references.balances, query, showInactive, usageFilter, belowMinimumOnly]);

  const locationsBySite = useMemo(() => {
    const groups = new Map<string, WarehouseLocation[]>();
    for (const location of references.locations) {
      if (!showInactive && !location.active) continue;
      groups.set(location.siteId, [...(groups.get(location.siteId) ?? []), location]);
    }
    return [...groups.entries()].sort(([a], [b]) =>
      references.siteName(a).localeCompare(references.siteName(b)),
    );
  }, [references, showInactive]);

  if (references.loading) return <PageLoadingState label="Loading stock…" />;
  if (references.error) {
    return (
      <ErrorState
        title="Stock could not be loaded"
        rpcName="ListStockItems"
        onRetry={references.refetch}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Stock"
        description="Consumables and small goods counted by quantity. Serialized equipment lives in Inventory."
        actions={
          tab === 'locations' ? (
            <button type="button" className="primary" onClick={() => setEditing('new-location')}>
              New location
            </button>
          ) : (
            <button type="button" className="primary" onClick={() => setEditing('new-item')}>
              New stock item
            </button>
          )
        }
      />
      {editing === 'new-item' ? <StockItemDrawer onClose={() => setEditing(null)} /> : null}
      {editing === 'new-location' ? (
        <LocationDrawer references={references} onClose={() => setEditing(null)} />
      ) : null}
      {editing && typeof editing === 'object' ? (
        <LocationDrawer
          location={editing}
          references={references}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <div className="region-tabs" role="tablist" aria-label="Stock views">
        {tabs.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={tab === candidate}
            className={`region-tab${tab === candidate ? ' is-active' : ''}`}
            onClick={() => setParam('tab', candidate === 'items' ? '' : candidate)}
          >
            {tabLabels[candidate]}
          </button>
        ))}
      </div>

      <section className="reference-workspace" aria-label={tabLabels[tab]}>
        {tab !== 'movements' ? (
          <form
            className="requests-controls"
            role="search"
            aria-label="Filter stock"
            onSubmit={(event) => event.preventDefault()}
          >
            {tab === 'items' ? (
              <label className="search-field">
                <span>Search</span>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setParam('q', event.target.value)}
                  placeholder="SKU, barcode or name"
                  aria-label="Search stock items"
                />
              </label>
            ) : null}
            {tab === 'items' ? (
              <label className="search-field">
                <span>Used for</span>
                <SearchableSelect
                  ariaLabel="Filter by use"
                  value={usageFilter ?? ''}
                  onChange={(value) => setParam('usage', value)}
                  options={[
                    { value: '', label: 'All uses' },
                    ...stockUsages.map((usage) => ({
                      value: usage,
                      label: stockUsageLabels[usage],
                    })),
                    { value: 'unset', label: 'Not set' },
                  ]}
                  placeholder="All uses"
                />
              </label>
            ) : null}
            {tab === 'items' ? (
              <label className="checkbox-field">
                <span>Below minimum</span>
                <input
                  type="checkbox"
                  checked={belowMinimumOnly}
                  onChange={(event) => setParam('belowMinimum', event.target.checked ? 'true' : '')}
                  aria-label="Below minimum only"
                />
              </label>
            ) : null}
            <label className="checkbox-field">
              <span>Show deactivated</span>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(event) => setParam('inactive', event.target.checked ? 'true' : '')}
                aria-label="Show deactivated"
              />
            </label>
          </form>
        ) : null}

        {tab === 'items' ? (
          items.length === 0 ? (
            <div className="empty-state">
              <strong>
                {references.items.length === 0 ? 'No stock items yet.' : 'No stock items match.'}
              </strong>
              <button type="button" className="primary" onClick={() => setEditing('new-item')}>
                New stock item
              </button>
            </div>
          ) : (
            <ul className="stock-cards">
              {items.map((item) => {
                const totals = stockTotals(references.balances, item.id);
                const unit = stockUnitLabels[item.stockUnit];
                const low = isBelowMinimum(item, totals.onHand);
                const photo = stockApi.stockItemImageUrl(item);
                return (
                  <li key={item.id} className={`stock-card${item.active ? '' : ' is-inactive'}`}>
                    <div className="stock-card-heading">
                      {/* Every card keeps the photo slot, so headings line up. */}
                      {photo ? (
                        <img src={photo} alt="" className="stock-card-photo" loading="lazy" />
                      ) : (
                        <span className="stock-card-photo stock-card-no-photo" aria-hidden="true" />
                      )}
                      <div className="stock-card-title">
                        <Link
                          to={`/stock/items/${encodeURIComponent(item.id)}`}
                          className="order-card-title"
                          title={item.displayName}
                        >
                          {item.displayName}
                        </Link>
                        <span className="mono">{item.sku}</span>
                      </div>
                    </div>
                    <dl className="stock-figures">
                      <div>
                        <dt>On hand</dt>
                        <dd>
                          {totals.onHand} {unit}
                        </dd>
                      </div>
                      <div>
                        <dt>Reserved</dt>
                        <dd>
                          {totals.allocated} {unit}
                        </dd>
                      </div>
                      <div>
                        <dt>Available</dt>
                        <dd className={totals.available <= 0 ? 'stock-empty' : undefined}>
                          {totals.available} {unit}
                        </dd>
                      </div>
                      {item.minimumQuantity > 0 ? (
                        <div>
                          <dt>Minimum</dt>
                          <dd>
                            {item.minimumQuantity} {unit}
                          </dd>
                        </div>
                      ) : null}
                    </dl>
                    <div className="stock-card-badges">
                      {item.usage ? (
                        <span className="state-badge">{stockUsageLabels[item.usage]}</span>
                      ) : null}
                      {low ? <span className="state-badge stock-low">Below minimum</span> : null}
                      {!item.active ? (
                        <span className="state-badge state-badge-muted">Deactivated</span>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )
        ) : null}

        {tab === 'locations' ? (
          locationsBySite.length === 0 ? (
            <div className="empty-state">
              <strong>No locations yet.</strong>
              <button type="button" className="primary" onClick={() => setEditing('new-location')}>
                New location
              </button>
            </div>
          ) : (
            locationsBySite.map(([siteId, locations]) => (
              <section key={siteId} className="dispatch-group" aria-labelledby={`site-${siteId}`}>
                <h2 id={`site-${siteId}`} className="dispatch-group-title">
                  {references.siteName(siteId)}
                </h2>
                <ul className="location-list">
                  {locations
                    .sort((a, b) =>
                      references.locationName(a.id).localeCompare(references.locationName(b.id)),
                    )
                    .map((location) => {
                      const held = references.balances.filter(
                        (b) => b.locationId === location.id && b.onHand !== 0,
                      ).length;
                      return (
                        <li
                          key={location.id}
                          className={`location-entry${location.active ? '' : ' is-inactive'}`}
                        >
                          <div>
                            <strong>{references.locationName(location.id)}</strong>{' '}
                            <span className="mono">{location.code}</span>{' '}
                            <span className="state-badge">
                              {locationTypeLabels[location.type as LocationType] ?? location.type}
                            </span>
                            {!location.active ? (
                              <span className="state-badge state-badge-muted"> Deactivated</span>
                            ) : null}
                          </div>
                          <div className="movement-meta">
                            {held === 0 ? 'Empty' : `${held} stock item${held === 1 ? '' : 's'}`}
                            <button
                              type="button"
                              className="link-button"
                              onClick={() => setEditing(location)}
                              aria-label={`Edit ${location.code}`}
                            >
                              Edit
                            </button>
                          </div>
                        </li>
                      );
                    })}
                </ul>
              </section>
            ))
          )
        ) : null}

        {tab === 'movements' ? <MovementList references={references} /> : null}
      </section>
    </>
  );
}
